import { env } from './env'
import { clearSession, getToken } from './session'

/**
 * The single place the dashboard talks to the API.
 *
 * Every screen goes through here, so authentication, error shape, timeouts and
 * session expiry are handled once instead of being re-invented per page.
 */

export type FieldErrors = Record<string, string>

/** A failed API call, normalised into something the interface can act on. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: FieldErrors | null

  constructor(message: string, status: number, code: string, fieldErrors: FieldErrors | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fieldErrors = fieldErrors
  }

  /** The caller is not signed in, or the token is no longer good. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Signed in, but not allowed — a non-admin account, or a suspended one. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** The request contradicts the current state of the record. */
  get isConflict(): boolean {
    return this.status === 409
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422
  }

  get isNetwork(): boolean {
    return this.status === 0
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Serialised as JSON. Use `formData` for uploads. */
  body?: unknown
  formData?: FormData
  /** Values that are `undefined`, `null` or `''` are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Skips the Authorization header — used by the login call. */
  anonymous?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`)

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

/**
 * The API answers with several error shapes depending on where the failure
 * happened: `{error}` from the admin controllers, `{message}` from the auth
 * controller, and a flat `{field: message}` map from bean validation. This
 * flattens all three into one result.
 */
function parseErrorPayload(payload: unknown, status: number): { message: string; fieldErrors: FieldErrors | null } {
  if (typeof payload === 'string' && payload.trim().length > 0) {
    return { message: payload, fieldErrors: null }
  }

  if (typeof payload === 'object' && payload !== null) {
    const record = payload as Record<string, unknown>

    const direct = record.error ?? record.message
    if (typeof direct === 'string' && direct.trim().length > 0) {
      return { message: direct, fieldErrors: null }
    }

    // Bean-validation responses are a plain map of field -> message.
    const fieldErrors: FieldErrors = {}
    for (const [key, value] of Object.entries(record)) {
      if (typeof value === 'string' && key !== 'timestamp' && key !== 'code' && key !== 'status') {
        fieldErrors[key] = value
      }
    }
    const entries = Object.entries(fieldErrors)
    if (entries.length > 0) {
      return { message: entries.map(([, message]) => message).join('. '), fieldErrors }
    }
  }

  return { message: defaultMessageForStatus(status), fieldErrors: null }
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'The request was rejected. Please check the values and try again.'
    case 401:
      return 'Your session has expired. Please sign in again.'
    case 403:
      return 'You do not have permission to perform this action.'
    case 404:
      return 'The requested record no longer exists.'
    case 409:
      return 'This action conflicts with the current state of the record.'
    case 413:
      return 'The uploaded file is too large.'
    case 500:
    case 502:
    case 503:
      return 'The server is temporarily unavailable. Please try again shortly.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, query, signal, anonymous = false } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (!anonymous) {
    const token = getToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  if (body !== undefined && !formData) {
    headers['Content-Type'] = 'application/json'
  }

  // A hung request should surface as a clear failure, not an endless spinner.
  const timeout = new AbortController()
  const timer = setTimeout(() => timeout.abort(), env.apiTimeoutMs)
  const abortSignal = signal ? anySignal([signal, timeout.signal]) : timeout.signal

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal: abortSignal,
    })
  } catch (error) {
    clearTimeout(timer)
    if (signal?.aborted) throw error
    if (timeout.signal.aborted) {
      throw new ApiError('The request timed out. Please check your connection and try again.', 0, 'TIMEOUT')
    }
    throw new ApiError(
      'Could not reach the Drive For Me API. Please check your connection and try again.',
      0,
      'NETWORK',
    )
  } finally {
    clearTimeout(timer)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const rawText = await response.text()
  let payload: unknown = rawText
  if (rawText.length > 0) {
    try {
      payload = JSON.parse(rawText)
    } catch {
      payload = rawText
    }
  }

  if (!response.ok) {
    const { message, fieldErrors } = parseErrorPayload(payload, response.status)

    // An expired or revoked token cannot be recovered from in the browser, so
    // the session is dropped here and the route guard sends the admin to login.
    if (response.status === 401) {
      clearSession()
    }

    const code =
      typeof payload === 'object' && payload !== null && typeof (payload as { code?: unknown }).code === 'string'
        ? (payload as { code: string }).code
        : `HTTP_${response.status}`

    throw new ApiError(message, response.status, code, fieldErrors)
  }

  return payload as T
}

/**
 * Fetches a binary response — a proof-of-payment image — as an object URL.
 *
 * Separate from {@link apiRequest} because that one reads the body as text and
 * parses it as JSON, which would corrupt an image and lose the content type.
 *
 * The reason this exists at all is that the endpoint requires an
 * `Authorization` header. A proof of payment shows a customer's bank account,
 * so it is deliberately not reachable by URL alone — which means it cannot be
 * put in an `<img src>` and has to be fetched, authenticated, and turned into a
 * blob URL here.
 *
 * **The caller must revoke the returned URL** when the image is no longer shown.
 * A blob URL pins its data in memory until it is revoked, and an operator
 * working through a queue of payments would otherwise accumulate every receipt
 * they had looked at.
 */
export async function apiBlobRequest(
  path: string,
  options: Omit<RequestOptions, 'method' | 'body' | 'formData'> = {},
): Promise<{ objectUrl: string; contentType: string }> {
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const timeout = new AbortController()
  const timer = setTimeout(() => timeout.abort(), env.apiTimeoutMs)
  const abortSignal = options.signal ? anySignal([options.signal, timeout.signal]) : timeout.signal

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), { headers, signal: abortSignal })
  } catch (error) {
    if (options.signal?.aborted) throw error
    if (timeout.signal.aborted) {
      throw new ApiError('The image took too long to load. Please try again.', 0, 'TIMEOUT')
    }
    throw new ApiError('Could not reach the Drive For Me API.', 0, 'NETWORK')
  } finally {
    clearTimeout(timer)
  }

  if (!response.ok) {
    // The error body is JSON even though the success body is not, so it is
    // still worth reading for the server's own wording.
    let payload: unknown = await response.text()
    try {
      payload = JSON.parse(payload as string)
    } catch {
      /* leave it as text */
    }
    if (response.status === 401) clearSession()
    const { message } = parseErrorPayload(payload, response.status)
    const code =
      typeof payload === 'object' && payload !== null && typeof (payload as { code?: unknown }).code === 'string'
        ? (payload as { code: string }).code
        : `HTTP_${response.status}`
    throw new ApiError(message, response.status, code)
  }

  const blob = await response.blob()
  return {
    objectUrl: URL.createObjectURL(blob),
    contentType: response.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream',
  }
}

/** `AbortSignal.any` is not available in every browser this must support. */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController()
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason)
      break
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true })
  }
  return controller.signal
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'method' | 'body' | 'formData'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', formData }),
}
